/**
 * QA Review Service
 *
 * API client for QA review CRUD operations.
 * Endpoints match backend/api/routes/qa_reviews.py
 */

import { apiClient } from './apiClient';
import type {
  QAReviewWithContent,
  QAReviewListResponse,
  QAReviewFilters,
  QAReviewUpsert,
} from '../types/qaReview';

export const qaReviewService = {
  /**
   * List QA reviews for a tenant with filtering and sorting
   */
  async list(tenantId: string, filters: QAReviewFilters = {}): Promise<QAReviewListResponse> {
    const params = new URLSearchParams({ tenant_id: tenantId });

    if (filters.qa_status && filters.qa_status !== 'all') {
      params.set('qa_status', filters.qa_status);
    }
    if (filters.date_from) params.set('date_from', filters.date_from);
    if (filters.date_to) params.set('date_to', filters.date_to);
    if (filters.search) params.set('search', filters.search);
    if (filters.wp_status) params.set('wp_status', filters.wp_status);
    if (filters.sort_by) params.set('sort_by', filters.sort_by);
    if (filters.sort_order) params.set('sort_order', filters.sort_order);
    if (filters.limit) params.set('limit', String(filters.limit));
    if (filters.offset !== undefined) params.set('offset', String(filters.offset));

    const response = await apiClient.get<QAReviewListResponse>(`/qa/reviews?${params}`);
    return response.data;
  },

  /**
   * Get single QA review for a content item
   */
  async get(contentId: string, tenantId: string): Promise<QAReviewWithContent> {
    const response = await apiClient.get<QAReviewWithContent>(`/qa/reviews/${contentId}?tenant_id=${tenantId}`);
    return response.data;
  },

  /**
   * Create or update (upsert) a QA review. Accepts partial updates.
   */
  async upsert(contentId: string, tenantId: string, data: QAReviewUpsert): Promise<QAReviewWithContent> {
    const response = await apiClient.put<QAReviewWithContent>(`/qa/reviews/${contentId}?tenant_id=${tenantId}`, data);
    return response.data;
  },

  /**
   * Delete/reset a QA review
   */
  async delete(contentId: string, tenantId: string): Promise<void> {
    await apiClient.delete(`/qa/reviews/${contentId}?tenant_id=${tenantId}`);
  },

  /**
   * Bulk mark reviews as reviewed (sets reviewed_at + reviewed_by).
   * Uses Promise.allSettled to handle partial failures gracefully.
   */
  async bulkMarkReviewed(
    contentIds: string[],
    tenantId: string,
    reviewedBy: string
  ): Promise<{ succeeded: number; failed: number }> {
    const now = new Date().toISOString();
    const results = await Promise.allSettled(
      contentIds.map((id) =>
        apiClient.put(`/qa/reviews/${id}?tenant_id=${tenantId}`, {
          reviewed_by: reviewedBy,
          reviewed_at: now,
        } as QAReviewUpsert)
      )
    );
    const succeeded = results.filter((r) => r.status === 'fulfilled').length;
    return { succeeded, failed: results.length - succeeded };
  },

  /**
   * Bulk clear all issue flags for selected reviews.
   * Resets every issue_* flag to false.
   */
  async bulkClearIssues(contentIds: string[], tenantId: string): Promise<{ succeeded: number; failed: number }> {
    const clearData: QAReviewUpsert = {
      issue_ext_links: false,
      issue_citations: false,
      issue_competitor_links: false,
      issue_int_links: false,
      issue_kw_density: false,
      issue_kw_first_para: false,
      issue_kw_heading: false,
      issue_meta_length: false,
      issue_meta_keyword: false,
      issue_meta_ctr: false,
      issue_cta: false,
      issue_structure: false,
      issue_featured_img: false,
    };
    const results = await Promise.allSettled(
      contentIds.map((id) => apiClient.put(`/qa/reviews/${id}?tenant_id=${tenantId}`, clearData))
    );
    const succeeded = results.filter((r) => r.status === 'fulfilled').length;
    return { succeeded, failed: results.length - succeeded };
  },
};
